import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../../context/AppContext';
import './AdBanner.css';

// Default ads shown when none are configured
const DEFAULT_ADS = [
  {
    id: 1,
    badge: '🔥 OFFRE LIMITÉE',
    title: 'Site Web Professionnel',
    subtitle: 'Création de site vitrine clé en main',
    desc: 'Design moderne, responsive, SEO optimisé. Livré en 7 jours.',
    price: 'À partir de 80 000 FCFA',
    priceSub: '≈ 133 $ · ≈ 122 €',
    cta: 'Demander un devis',
    ctaHref: '#contact',
    color: 'blue',
    urgency: '',
    emoji: '💻',
  },
  {
    id: 2,
    badge: '⚡ NOUVEAU',
    title: 'Formation Data Science',
    subtitle: 'Python · Machine Learning · Tableau de bord',
    desc: 'Certification KOTOSTECHNOLOGY. 2 mois de pratique intensive.',
    price: '120 000 FCFA',
    priceSub: '≈ 200 $ · ≈ 183 €',
    cta: 'S\'inscrire maintenant',
    ctaHref: '#formations',
    color: 'gold',
    urgency: '⏰ Places limitées — 12 participants max',
    emoji: '📊',
  },
  {
    id: 3,
    badge: '☀️ ÉNERGIE SOLAIRE',
    title: 'Kit Solaire Complet',
    subtitle: 'Installation + Maintenance + Garantie',
    desc: 'Dimensionnement gratuit. Pose par nos techniciens certifiés.',
    price: 'À partir de 500 000 FCFA',
    priceSub: '≈ 833 $ · ≈ 763 €',
    cta: 'Obtenir un devis gratuit',
    ctaHref: '#contact',
    color: 'green',
    urgency: '',
    emoji: '⚡',
  },
];

// ── Single ad card ─────────────────────────────────────
function AdCard({ ad, active, total, onNext, onPrev }) {
  return (
    <div className={`ad-card ad-${ad.color} ${active ? 'ad-active' : ''}`}>
      <div className="ad-inner">
        {/* Left decoration */}
        <div className="ad-deco">
          <div className="ad-emoji">{ad.emoji}</div>
          <div className="ad-lines">
            <div/><div/><div/>
          </div>
        </div>

        {/* Content */}
        <div className="ad-content">
          <div className="ad-badge-row">
            <span className="ad-badge">{ad.badge}</span>
            {ad.urgency && <span className="ad-urgency">{ad.urgency}</span>}
          </div>
          <h2 className="ad-title">{ad.title}</h2>
          <p className="ad-subtitle">{ad.subtitle}</p>
          <p className="ad-desc">{ad.desc}</p>
          <div className="ad-price-row">
            <div>
              <div className="ad-price">{ad.price}</div>
              <div className="ad-price-sub">{ad.priceSub}</div>
            </div>
            <a href={ad.ctaHref} className={`ad-cta ad-cta-${ad.color}`}>
              {ad.cta} →
            </a>
          </div>
        </div>

        {/* Right nav */}
        <div className="ad-nav">
          <button className="ad-nav-btn" onClick={onPrev}>‹</button>
          <div className="ad-dots">
            {Array.from({ length: total }).map((_, i) => (
              <div key={i} className={`ad-dot ${i === 0 && active ? 'active' : ''}`} />
            ))}
          </div>
          <button className="ad-nav-btn" onClick={onNext}>›</button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="ad-progress">
        <div className="ad-progress-bar" key={ad.id} />
      </div>
    </div>
  );
}

// ── Admin editor ───────────────────────────────────────
function AdEditor({ ads, onSave, onClose }) {
  const [list, setList] = useState(ads.map(a => ({ ...a })));
  const [editing, setEditing] = useState(null);
  const { showToast } = useApp();

  function updateAd(i, key, val) {
    setList(l => l.map((a, j) => j === i ? { ...a, [key]: val } : a));
  }

  function addAd() {
    const newAd = {
      id: Date.now(),
      badge: '🆕 NOUVEAU',
      title: 'Titre de la publicité',
      subtitle: 'Sous-titre',
      desc: 'Description de l\'offre',
      price: 'Prix',
      priceSub: '',
      cta: 'En savoir plus',
      ctaHref: '#contact',
      color: 'blue',
      urgency: '',
      emoji: '📢',
    };
    setList(l => [...l, newAd]);
    setEditing(list.length);
  }

  function removeAd(i) {
    setList(l => l.filter((_, j) => j !== i));
    if (editing === i) setEditing(null);
  }

  const colors = ['blue', 'gold', 'green'];
  const emojis = ['💻','📊','☀️','🔩','🏡','🖨️','🎓','📢','🔥','⚡','🌟','💰'];

  return (
    <div className="ad-editor-overlay">
      <div className="ad-editor-box">
        <div className="ae-head">
          <h2>📢 Gestion des Publicités</h2>
          <div style={{ display:'flex', gap:8 }}>
            <button className="abtn abtn-gold" onClick={() => { onSave(list); showToast('Publicités sauvegardées !'); onClose(); }}>
              ✓ Sauvegarder
            </button>
            <button className="mclose" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="ae-body">
          {/* Ad list */}
          <div className="ae-list">
            <div className="ae-list-head">
              <span>Publicités ({list.length})</span>
              <button className="abtn abtn-green" style={{fontSize:12,padding:'5px 12px'}} onClick={addAd}>+ Ajouter</button>
            </div>
            {list.map((ad, i) => (
              <div
                key={ad.id}
                className={`ae-item ${editing === i ? 'ae-item-active' : ''}`}
                onClick={() => setEditing(i)}
              >
                <span>{ad.emoji}</span>
                <div className="ae-item-info">
                  <div className="ae-item-title">{ad.title || 'Sans titre'}</div>
                  <div className="ae-item-price">{ad.price}</div>
                </div>
                <span className={`ae-color-dot ae-color-${ad.color}`}/>
                <button className="ae-del" onClick={e => { e.stopPropagation(); removeAd(i); }}>✕</button>
              </div>
            ))}
          </div>

          {/* Editor form */}
          {editing !== null && list[editing] && (
            <div className="ae-form">
              <h3>✏️ Modifier : {list[editing].title}</h3>

              <div className="ae-row">
                <div className="fg2">
                  <label>Emoji</label>
                  <div className="emoji-picker">
                    {emojis.map(e => (
                      <button key={e} className={`emoji-opt ${list[editing].emoji === e ? 'active' : ''}`}
                        onClick={() => updateAd(editing, 'emoji', e)}>{e}</button>
                    ))}
                  </div>
                </div>
                <div className="fg2">
                  <label>Couleur thème</label>
                  <div style={{ display:'flex', gap:8 }}>
                    {colors.map(c => (
                      <button key={c} className={`color-opt color-opt-${c} ${list[editing].color === c ? 'active' : ''}`}
                        onClick={() => updateAd(editing, 'color', c)}>{c}</button>
                    ))}
                  </div>
                </div>
              </div>

              {[
                ['badge',    'Badge (ex: 🔥 OFFRE LIMITÉE)'],
                ['title',    'Titre principal *'],
                ['subtitle', 'Sous-titre'],
                ['desc',     'Description'],
                ['price',    'Prix affiché *'],
                ['priceSub', 'Prix en USD/EUR (ex: ≈ 133 $ · ≈ 122 €)'],
                ['cta',      'Texte du bouton'],
                ['ctaHref',  'Lien du bouton (ex: #contact)'],
                ['urgency',  'Message d\'urgence (ex: ⏰ 5 places restantes)'],
              ].map(([key, lbl]) => (
                <div key={key} className="fg2">
                  <label>{lbl}</label>
                  <input
                    value={list[editing][key] || ''}
                    onChange={e => updateAd(editing, key, e.target.value)}
                    placeholder={lbl}
                  />
                </div>
              ))}
            </div>
          )}
          {editing === null && (
            <div className="ae-empty">
              <div style={{fontSize:48,marginBottom:12}}>📢</div>
              <p>Cliquez sur une publicité pour la modifier</p>
              <p style={{fontSize:13,color:'var(--muted)'}}>
                Les publicités défilent automatiquement toutes les 6 secondes sur le site.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────
export default function AdBanner() {
  const { currentUser, data, updateData } = useApp();
  const [currentIdx, setCurrentIdx] = useState(0);
  const [showEditor, setShowEditor] = useState(false);
  const timerRef = useRef(null);

  const ads = (data.ads && data.ads.length > 0) ? data.ads : DEFAULT_ADS;
  const total = ads.length;

  // Auto-advance
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCurrentIdx(i => (i + 1) % total);
    }, 6500);
    return () => clearInterval(timerRef.current);
  }, [total]);

  function goNext() {
    clearInterval(timerRef.current);
    setCurrentIdx(i => (i + 1) % total);
  }
  function goPrev() {
    clearInterval(timerRef.current);
    setCurrentIdx(i => (i - 1 + total) % total);
  }

  function saveAds(newAds) {
    updateData({ ads: newAds });
  }

  return (
    <section className="ad-section">
      {/* Section header */}
      <div className="ad-header">
        <div className="ad-header-left">
          <div className="ad-live-dot" />
          <span className="ad-live-label">OFFRES &amp; PROMOTIONS EN COURS</span>
        </div>
        {currentUser?.role === 'admin' && (
          <button className="ad-edit-btn" onClick={() => setShowEditor(true)}>
            ✏️ Gérer les publicités
          </button>
        )}
      </div>

      {/* Carousel */}
      <div className="ad-carousel">
        {ads.map((ad, i) => (
          <div
            key={ad.id}
            className={`ad-slide ${i === currentIdx ? 'ad-slide-active' : ''}`}
          >
            <AdCard
              ad={ad}
              active={i === currentIdx}
              total={total}
              onNext={goNext}
              onPrev={goPrev}
            />
          </div>
        ))}
      </div>

      {/* Dot indicators */}
      <div className="ad-indicators">
        {ads.map((_, i) => (
          <button
            key={i}
            className={`ad-ind-btn ${i === currentIdx ? 'active' : ''}`}
            onClick={() => { clearInterval(timerRef.current); setCurrentIdx(i); }}
          />
        ))}
      </div>

      {/* Admin editor modal */}
      {showEditor && (
        <AdEditor
          ads={ads}
          onSave={saveAds}
          onClose={() => setShowEditor(false)}
        />
      )}
    </section>
  );
}
